import React, {
    createContext,
    useContext,
    useRef,
    useEffect,
    useState,
    useMemo,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import io from 'socket.io-client'

import { getSession } from '../store/auth/authActions'
import { handleSocketIncomingMessage } from '../store/chat/chatActions'
import { inAppReceived } from '../store/notification/notificationSlice'
import { fetchInAppUnreadCount } from '../store/notification/notificationActions'

export const SocketContext = createContext()

const IN_APP_NOTIFICATION = 'in_app_notification'

const SOCKET_DEBUG =
    process.env.REACT_APP_DEBUG_SOCKET === '1' ||
    process.env.REACT_APP_DEBUG_SOCKET === 'true'

const SOCKET_LABEL =
    process.env.REACT_APP_SOCKET_DEBUG_LABEL || 'frontend'

function socketLog(...parts) {
    if (!SOCKET_DEBUG) return
    console.log(`[socket:${SOCKET_LABEL}]`, ...parts)
}

function tokenHint(t) {
    if (!t || typeof t !== 'string') return '(none)'
    if (t.length <= 16) return `(len=${t.length})`
    return `${t.slice(0, 6)}…${t.slice(-4)} (len=${t.length})`
}

/** Access token payload includes `profile` (ObjectId) before Redux has full `currentProfile._id`. */
function profileIdFromJwt(token) {
    if (!token || typeof token !== 'string') return ''
    const parts = token.split('.')
    if (parts.length < 2) return ''
    try {
        const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : ''
        const payload = JSON.parse(atob(b64 + pad))
        const p = payload?.profile
        if (p == null || p === '') return ''
        if (typeof p === 'string') return p
        if (typeof p === 'object' && p != null && '$oid' in p) return String(p.$oid)
        return String(p)
    } catch {
        return ''
    }
}

/** Socket.IO origin: REACT_APP_SOCKET_DOMAIN first, then fallbacks (see plan). */
function resolveSocketBaseUrl() {
    const trim = (u) => String(u || '').trim().replace(/\/+$/, '')
    const stripApi = (u) => trim(u).replace(/\/api\/v1\/?$/i, '')
    return (
        stripApi(process.env.REACT_APP_SOCKET_DOMAIN || '') ||
        trim(process.env.REACT_APP_SOCKET_URL || '') ||
        trim(process.env.REACT_APP_BACKEND_URL || '') ||
        (process.env.REACT_APP_BACKEND_DOMAIN
            ? stripApi(process.env.REACT_APP_BACKEND_DOMAIN)
            : '')
    )
}

export const SocketProvider = ({ children }) => {
    const [socketInstance, setSocketInstance] = useState(null)
    const dispatch = useDispatch()
    const refreshAfterErrorRef = useRef(false)

    const socketUrl = useMemo(() => resolveSocketBaseUrl(), [])

    const profileIdFromStore = useSelector((state) => {
        const cp = state.profile?.currentProfile
        const id = cp?._id ?? cp?.id
        if (id != null && String(id).trim() !== '') return String(id)
        return ''
    })
    const accessToken = useSelector((state) => state.auth?.accessToken ?? '')

    const socket = useRef(null)

    const handleOnMessage = (payload) => {
        dispatch(handleSocketIncomingMessage(payload))
    }

    const handleInAppNotification = (payload) => {
        const n = payload?.notification
        if (n?.id) {
            dispatch(inAppReceived(n))
        }
    }

    useEffect(() => {
        const token =
            (typeof accessToken === 'string' && accessToken.length > 0
                ? accessToken
                : null) || localStorage.getItem('auth_token')

        const profileId =
            (profileIdFromStore && profileIdFromStore.trim()) ||
            profileIdFromJwt(token || '')

        socketLog('effect run', {
            url: socketUrl || '(empty)',
            profileId: profileId ? String(profileId) : '(none)',
            profileFromStore: profileIdFromStore || '(none)',
            client: 'app',
            token: tokenHint(token),
            hasLocalStorageAuthToken: Boolean(localStorage.getItem('auth_token')),
        })

        if (!socketUrl || !profileId || !token) {
            socketLog('skip io()', {
                reason: !socketUrl
                    ? 'missing_socket_url'
                    : !profileId
                      ? 'missing_profile_id'
                      : 'missing_token',
            })
            if (socket.current) {
                socket.current.disconnect()
                socket.current = null
            }
            setSocketInstance(null)
            return
        }

        if (socket.current) {
            socket.current.disconnect()
            socket.current = null
        }

        refreshAfterErrorRef.current = false

        socketLog('io() connecting…', { url: socketUrl })

        const nextSocket = io(socketUrl, {
            transports: ['websocket', 'polling'],
            withCredentials: true,
            query: {
                token,
                profile: profileId,
                client: 'app',
            },
        })

        socket.current = nextSocket
        setSocketInstance(nextSocket)

        nextSocket.on('connect', () => {
            refreshAfterErrorRef.current = false
            const transport = nextSocket.io?.engine?.transport?.name
            socketLog('connected', {
                id: nextSocket.id,
                transport: transport || '(unknown)',
            })
            void dispatch(fetchInAppUnreadCount())
            nextSocket.emit('SOCKET_INFO', {
                id: nextSocket.id,
                profile: profileId,
            })
        })

        nextSocket.on('disconnect', (reason) => {
            socketLog('disconnected', { reason })
        })

        nextSocket.on('connect_error', (err) => {
            socketLog('connect_error', {
                message: err?.message,
                description: err?.description,
                data: err?.data,
            })
            if (refreshAfterErrorRef.current) return
            refreshAfterErrorRef.current = true
            socketLog('dispatch getSession() after connect_error')
            void dispatch(getSession())
                .unwrap()
                .then(() => socketLog('getSession fulfilled, effect will reconnect'))
                .catch((e) =>
                    socketLog('getSession rejected', e?.message || String(e)),
                )
        })

        if (SOCKET_DEBUG && nextSocket.io) {
            nextSocket.io.on('reconnect_attempt', (n) =>
                socketLog('reconnect_attempt', n),
            )
            nextSocket.io.on('error', (e) => socketLog('manager error', e))
        }

        nextSocket.on('message', handleOnMessage)
        nextSocket.on(IN_APP_NOTIFICATION, handleInAppNotification)

        return () => {
            socketLog('cleanup disconnect')
            nextSocket.off('message', handleOnMessage)
            nextSocket.off(IN_APP_NOTIFICATION, handleInAppNotification)
            nextSocket.disconnect()
            if (socket.current === nextSocket) {
                socket.current = null
            }
            setSocketInstance(null)
        }
    }, [profileIdFromStore, socketUrl, accessToken, dispatch])

    return (
        <SocketContext.Provider value={socketInstance}>
            {children}
        </SocketContext.Provider>
    )
}

export const useSocket = () => useContext(SocketContext)
